import { useState, useEffect } from 'react'
import { Database, Play, RefreshCw, Clock, Download, Info, Zap, HardDrive, Calendar, TrendingUp } from 'lucide-react'
import api from '../services/api'

const DuckDBExplorer = () => {
  const [stats, setStats] = useState(null)
  const [schema, setSchema] = useState(null)
  const [sampleQueries, setSampleQueries] = useState([])
  const [queryInput, setQueryInput] = useState('')
  const [queryResults, setQueryResults] = useState(null)
  const [syncStatus, setSyncStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [executing, setExecuting] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [activeTab, setActiveTab] = useState('query') // 'query', 'schema', 'samples', 'sync'

  useEffect(() => {
    loadInitialData()
    const interval = setInterval(checkSyncStatus, 3000) // Poll sync status every 3 seconds
    return () => clearInterval(interval)
  }, [])

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadStats(),
        loadSchema(),
        loadSampleQueries(),
        checkSyncStatus()
      ])
    } catch (error) {
      console.error('Error loading initial data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadStats = async () => {
    try {
      const data = await api.get('/duckdb/stats')
      setStats(data)
    } catch (error) {
      console.error('Error loading stats:', error)
    }
  }

  const loadSchema = async () => {
    try {
      const data = await api.get('/duckdb/schema')
      setSchema(data)
    } catch (error) {
      console.error('Error loading schema:', error)
    }
  }

  const loadSampleQueries = async () => {
    try {
      const data = await api.get('/duckdb/sample-queries')
      setSampleQueries(data.queries || [])
    } catch (error) {
      console.error('Error loading sample queries:', error)
    }
  }

  const checkSyncStatus = async () => {
    try {
      const data = await api.get('/duckdb/sync/status')
      setSyncStatus(data)

      // Reload stats if sync just completed
      if (data && !data.is_syncing && syncStatus?.is_syncing) {
        loadStats()
      }
    } catch (error) {
      console.error('Error checking sync status:', error)
    }
  }

  const executeQuery = async () => {
    if (!queryInput.trim()) {
      alert('Please enter a query')
      return
    }

    setExecuting(true)
    try {
      const data = await api.post('/duckdb/query', {
        query: queryInput,
        limit: 1000
      })
      setQueryResults(data)
    } catch (error) {
      alert(`Query failed: ${error.response?.data?.detail || error.message}`)
      setQueryResults(null)
    } finally {
      setExecuting(false)
    }
  }

  const loadSampleQuery = (sql) => {
    setQueryInput(sql)
    setActiveTab('query')
  }

  const triggerSync = async (syncType) => {
    if (!confirm(`Start ${syncType} sync? This may take 1-3 minutes.`)) return

    setSyncing(true)
    try {
      await api.post('/duckdb/sync', {
        sync_type: syncType,
        days: syncType === 'incremental' ? 7 : undefined
      })
      alert(`${syncType.charAt(0).toUpperCase() + syncType.slice(1)} sync started successfully`)
    } catch (error) {
      alert(`Failed to start sync: ${error.response?.data?.detail || error.message}`)
    } finally {
      setSyncing(false)
    }
  }

  const exportToCSV = () => {
    if (!queryResults || !queryResults.rows.length) {
      alert('No data to export')
      return
    }

    const csv = [
      queryResults.columns.join(','),
      ...queryResults.rows.map(row => row.map(cell =>
        typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
      ).join(','))
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `duckdb_query_${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">DuckDB Feature Store Explorer</h1>
        </div>
        <p className="text-gray-600">Query and manage the ML training feature store (50-70x faster than PostgreSQL)</p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Total Rows</div>
              <Database className="w-5 h-5 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_rows.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">{stats.table_name}</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Unique Tickers</div>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.unique_tickers.toLocaleString()}</div>
            <div className="text-xs text-gray-500 mt-1">Stock symbols</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Date Range</div>
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <div className="text-sm font-bold text-gray-900">{stats.min_date}</div>
            <div className="text-sm font-bold text-gray-900">{stats.max_date}</div>
          </div>

          <div className="bg-white rounded-lg shadow p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium text-gray-600">Database Size</div>
              <HardDrive className="w-5 h-5 text-orange-500" />
            </div>
            <div className="text-2xl font-bold text-gray-900">{stats.database_size_mb.toLocaleString()} MB</div>
            <div className="text-xs text-gray-500 mt-1">Columnar storage</div>
          </div>
        </div>
      )}

      {/* Sync Status Banner */}
      {syncStatus && syncStatus.is_syncing && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
          <div className="flex-1">
            <div className="font-semibold text-blue-900">Sync in Progress</div>
            <div className="text-sm text-blue-700">{syncStatus.progress}</div>
            <div className="text-xs text-blue-600 mt-1">Started: {new Date(syncStatus.started_at).toLocaleTimeString()}</div>
          </div>
        </div>
      )}

      {/* Sync Error Banner */}
      {syncStatus && syncStatus.error && !syncStatus.is_syncing && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <Info className="w-5 h-5 text-red-600" />
          <div className="flex-1">
            <div className="font-semibold text-red-900">Sync Failed</div>
            <div className="text-sm text-red-700">{syncStatus.error}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200 flex">
          <button
            onClick={() => setActiveTab('query')}
            className={`px-6 py-3 font-medium ${activeTab === 'query' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            SQL Query
          </button>
          <button
            onClick={() => setActiveTab('samples')}
            className={`px-6 py-3 font-medium ${activeTab === 'samples' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Sample Queries
          </button>
          <button
            onClick={() => setActiveTab('schema')}
            className={`px-6 py-3 font-medium ${activeTab === 'schema' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Table Schema
          </button>
          <button
            onClick={() => setActiveTab('sync')}
            className={`px-6 py-3 font-medium ${activeTab === 'sync' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600 hover:text-gray-900'}`}
          >
            Sync Management
          </button>
        </div>

        <div className="p-6">
          {/* SQL Query Tab */}
          {activeTab === 'query' && (
            <div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">SQL Query (read-only)</label>
                <textarea
                  value={queryInput}
                  onChange={(e) => setQueryInput(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg font-mono text-sm"
                  rows="6"
                  placeholder="SELECT * FROM ml_training_features LIMIT 100"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Only SELECT queries allowed. Results limited to 1,000 rows.
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={executeQuery}
                  disabled={executing || !queryInput.trim()}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {executing ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Executing...
                    </>
                  ) : (
                    <>
                      <Play className="w-4 h-4" />
                      Execute Query
                    </>
                  )}
                </button>

                {queryResults && (
                  <button
                    onClick={exportToCSV}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export CSV
                  </button>
                )}

                <button
                  onClick={() => { setQueryInput(''); setQueryResults(null); }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Clear
                </button>
              </div>

              {/* Query Results */}
              {queryResults && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="text-sm font-medium text-gray-700">
                        Results: {queryResults.row_count} rows
                      </div>
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Zap className="w-4 h-4" />
                        {queryResults.execution_time_ms}ms
                      </div>
                    </div>
                  </div>

                  <div className="border rounded-lg overflow-auto max-h-96">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          {queryResults.columns.map((col, idx) => (
                            <th key={idx} className="px-4 py-2 text-left font-medium text-gray-700 border-b">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {queryResults.rows.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50 border-b">
                            {row.map((cell, cellIdx) => (
                              <td key={cellIdx} className="px-4 py-2 text-gray-900">
                                {cell === null ? <span className="text-gray-400 italic">NULL</span> : String(cell)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Sample Queries Tab */}
          {activeTab === 'samples' && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Click on a sample query to load it into the query editor
              </div>
              {sampleQueries.map((sample, idx) => (
                <div key={idx} className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer" onClick={() => loadSampleQuery(sample.sql)}>
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-gray-900">{sample.name}</div>
                      <div className="text-sm text-gray-600 mt-1">{sample.description}</div>
                    </div>
                    <Play className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  </div>
                  <div className="mt-3 bg-gray-50 rounded p-3 text-xs font-mono text-gray-700 overflow-x-auto">
                    {sample.sql}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Schema Tab */}
          {activeTab === 'schema' && schema && (
            <div>
              <div className="text-sm text-gray-600 mb-4">
                Table: <span className="font-semibold">ml_training_features</span>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Column Name</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Data Type</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-700">Nullable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {schema.columns.map((col, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono text-gray-900">{col.name}</td>
                        <td className="px-4 py-3 text-gray-700">{col.type}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs ${col.nullable === 'YES' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                            {col.nullable}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sync Management Tab */}
          {activeTab === 'sync' && (
            <div>
              <div className="space-y-6">
                {/* Current Status */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Status</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {syncStatus?.is_syncing ? (
                      <div className="flex items-center gap-3">
                        <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                        <div>
                          <div className="font-medium text-gray-900">Sync in Progress</div>
                          <div className="text-sm text-gray-600">{syncStatus.progress}</div>
                          <div className="text-xs text-gray-500 mt-1">Type: {syncStatus.sync_type}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-green-600" />
                        <div>
                          <div className="font-medium text-gray-900">Ready</div>
                          <div className="text-sm text-gray-600">No sync operation in progress</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sync Actions */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Trigger Sync</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Incremental Sync */}
                    <div className="border rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <RefreshCw className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">Incremental Sync</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Sync last 7 days of data from PostgreSQL to DuckDB. Fast and efficient for daily updates.
                      </p>
                      <div className="text-xs text-gray-500 mb-4">
                        Estimated duration: 1-2 minutes
                      </div>
                      <button
                        onClick={() => triggerSync('incremental')}
                        disabled={syncing || syncStatus?.is_syncing}
                        className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Start Incremental Sync
                      </button>
                    </div>

                    {/* Full Sync */}
                    <div className="border rounded-lg p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="w-5 h-5 text-purple-600" />
                        <h4 className="font-semibold text-gray-900">Full Sync</h4>
                      </div>
                      <p className="text-sm text-gray-600 mb-4">
                        Complete refresh of all data. Use for initial setup or after major data changes.
                      </p>
                      <div className="text-xs text-gray-500 mb-4">
                        Estimated duration: 2-3 minutes
                      </div>
                      <button
                        onClick={() => triggerSync('full')}
                        disabled={syncing || syncStatus?.is_syncing}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Start Full Sync
                      </button>
                    </div>
                  </div>
                </div>

                {/* Automated Schedule Info */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Automated Schedule</h3>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="text-sm text-blue-900">
                        <div className="font-medium mb-2">DuckDB is automatically synchronized:</div>
                        <ul className="space-y-1 ml-4 list-disc">
                          <li>Daily incremental sync at 7:00 AM (last 7 days)</li>
                          <li>Weekly full sync every Sunday at 2:00 AM (complete refresh)</li>
                        </ul>
                        <div className="mt-2 text-xs text-blue-700">
                          Manage automated schedules in the Cron Jobs page
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default DuckDBExplorer
